// ReviewScreen.jsx
import React, { useContext, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import { FormContext } from '../../context/FormContext';
import { calculateInvoice } from '../../utils/calculateInvoice';
import { pdf } from '@react-pdf/renderer';
import InvoicePdf from './InvoicePDF';
import ContentBox from './ContentBox';
import { ArrowForward } from '@mui/icons-material';
import { getToken } from 'firebase/app-check';
import { appCheck } from '../../firebase';

export default function ReviewScreen() {
  const { formData } = useContext(FormContext);
  const navigate = useNavigate();
  const result = useMemo(() => calculateInvoice(formData), [formData]);
  
  const viewPdf = async () => {
    const blob = await pdf(<InvoicePdf data={formData} result={result} />).toBlob();
    const url = URL.createObjectURL(blob);
    window.open(url);
  };

  // Blob -> **raw** base64 (Mailjet needs raw, not a data URL)
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result; // "data:application/pdf;base64,AAAA..."
        const base64 = String(dataUrl).split('base64,')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const emailInvoice = async () => {
    try {
      
      // 1) Render PDF to Blob
      const blob = await pdf(<InvoicePdf data={formData} result={result} />).toBlob();

      // 2) Convert to raw base64
      const base64Pdf = await blobToBase64(blob);

      // 3) Build payload for your Mailjet endpoint
      const filename = `Invoice-${formData.invoiceNumber || formData.igHandle || 'user'}.pdf`;

      const creativeLabour = result?.creativeLabourValue ?? '0';
      const contentRevenue = result?.contentRevenue ?? '0';
      const doomscroll = result?.scrollTimeValue ?? '0';
      const wellbeing =  '6000';
      const amount = result?.total ?? '0';

      const fromName = formData?.fullName ?? '';
      const handle = formData?.igHandle ?? '';

      const invoice = formData?.invoiceNumber ?? '';

      const templateString = `
      <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <title>Invoice ${invoice}</title>
          <style>
            body, table, td, p {
              margin: 0;
              padding: 0;
            }
            body {
              width: 100% !important;
              -webkit-text-size-adjust: 100%;
              -ms-text-size-adjust: 100%;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
              background-color: #f3f4f6;
              color: #111827;
            }
            img {
              border: 0;
              outline: none;
              text-decoration: none;
              display: block;
              max-width: 100%;
              height: auto;
            }
            a {
              color: #2563eb;
              text-decoration: none;
            }
            .wrapper {
              width: 100%;
              table-layout: fixed;
              padding: 24px 0;
            }
            .card {
              max-width: 600px;
              margin: 0 auto;
              background-color: #ffffff;
              border-radius: 6px;
              overflow: hidden;
              border: 1px solid #e5e7eb;
            }
            .inner {
              padding: 24px 32px;
            }
            .logo-wrap {
              text-align: center;
              margin-bottom: 16px;
            }
            .logo {
              width: 164px;
              height: auto;
              margin: 16px auto;
            }
            .company-name {
              font-size: 18px;
              font-weight: 700;
              text-align: center;
              margin-bottom: 8px;
              color: #111827;
            }
            .amount-main {
              text-align: center;
              font-size: 32px;
              font-weight: 700;
              margin: 0;
            }
            .amount-currency {
              font-size: 14px;
              font-weight: 600;
              margin-left: 4px;
            }
            .meta-top {
              text-align: center;
              margin: 8px 0 16px;
              font-size: 13px;
              color: #4b5563;
            }
            .meta-top strong {
              display: block;
              font-weight: 700;
              color: #111827;
            }
            .primary-btn-wrap {
              text-align: center;
              margin: 8px 0 24px;
            }
            .primary-btn {
              display: inline-block;
              background-color: #2563eb;
              color: #ffffff !important;
              font-size: 14px;
              font-weight: 600;
              padding: 12px 28px;
              border-radius: 4px;
            }
            .body-text {
              font-size: 13px;
              line-height: 1.6;
              color: #111827;
              margin-bottom: 16px;
            }
            .body-text a {
              text-decoration: underline;
              word-break: break-all;
            }
            .greeting {
              margin-bottom: 8px;
            }
            .closing {
              margin-top: 16px;
              margin-bottom: 24px;
            }
            .line-table-title {
              font-size: 13px;
              font-weight: 700;
              margin-bottom: 8px;
              color: #111827;
            }
            .line-items-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 13px;
              margin-bottom: 24px;
            }
            .line-items-table th,
            .line-items-table td {
              padding: 6px 0;
            }
            .line-items-table th {
              text-align: left;
              font-weight: 600;
              color: #4b5563;
              border-bottom: 1px solid #e5e7eb;
            }
            .line-items-table th.amount {
              text-align: right !important;
            }
            .line-items-table td.amount {
              text-align: right;
              white-space: nowrap;
            }
            .line-items-table .label {
              font-weight: 600;
              padding-top: 8px;
            }
            .total-row {
              font-weight: 700;
              font-size: 14px;
              border-top: 1px solid #e5e7eb;
              padding-top: 8px;
            }
            .total-label {
              padding-top: 8px;
            }
            .total-amount {
              text-align: right;
              white-space: nowrap;
            }
            .bottom-btn-wrap {
              text-align: center;
              margin-top: 16px;
            }
            .bottom-btn {
              display: inline-block;
              background-color: #2563eb;
              color: #ffffff !important;
              font-size: 14px;
              font-weight: 600;
              padding: 12px 28px;
              border-radius: 4px;
            }
            @media only screen and (max-width: 600px) {
              .inner {
                padding: 20px 16px;
              }
              .amount-main {
                font-size: 26px;
              }
            }
          </style>
        </head>
        <body>
          <table class="wrapper" role="presentation" cellspacing="0" cellpadding="0" border="0">
            <tr>
              <td align="center">
                <table class="card" role="presentation" cellspacing="0" cellpadding="0" border="0">
                  <tr>
                    <td class="inner">
                      <!-- Top summary block -->
                      <div class="logo-wrap">
                        <img src="https://meta-invoice.com/images/Invoice-Instagram-Logo.png" class="logo" />
                      </div>
                      <p class="amount-main">
                        USD $${amount}
                      </p>
                      <p class="meta-top">
                        <strong>Due within 30 days of issue date</strong>
                        Invoice #: ${invoice}
                      </p>

                      <!-- Copy block -->
                      <p class="body-text greeting">
                        Hi,
                      </p>
                      <p class="body-text">
                        Here's invoice ${invoice} for USD $${amount}.
                      </p>
                      <p class="body-text">
                        The amount outstanding of USD $${amount} is due within 30 days of receiving this notice.
                      </p>
                      <p class="body-text closing">
                        If you have any questions, please let us know.<br /><br />
                        Thanks,<br />
                        Meta Invoice
                      </p>

                      <!-- Line items -->
                      <p class="line-table-title">Description</p>
                      <table class="line-items-table" role="presentation">
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th class="amount">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- Example rows, adapt as needed -->
                          <tr>
                            <td>Content Revenue</td>
                            <td class="amount">USD $${contentRevenue}</td>
                          </tr>
                          <tr>
                            <td>Doomscroll Value</td>
                            <td class="amount">USD $${doomscroll}</td>
                          </tr>
                          <tr>
                            <td>Creative Labour Value</td>
                            <td class="amount">USD $${creativeLabour}</td>
                          </tr>
                          <tr>
                            <td>Wellbeing Damages</td>
                            <td class="amount">USD $${wellbeing}</td>
                          </tr>
                          <tr class="total-row">
                            <td class="total-label">Amount Due</td>
                            <td class="total-amount">USD $${amount}</td>
                          </tr>
                        </tbody>
                      </table>

                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
        </html>
        `;

      const payload = {
        subject: `Invoice #${invoice} from @${handle} is due`,
        text: `Invoice amount: $${amount}`,
        html: templateString.replace(/\s+/g, ' ').trim(),
        filename,
        base64Pdf,
        handle,
      };

      const { token } = await getToken(appCheck, /* forceRefresh */ false);

      // 4) Send via your backend
      const res = await fetch('/api/send-invoice', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Firebase-AppCheck': token
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Mail send failed');
      }

      navigate('/success');
    } catch (err) {
      console.error(err);
      alert('Sorry — emailing the invoice failed. Please try again.');
    }
  };

  const shareCard = () => {
    const card = document.getElementById('share-card');
    if (!card) return;

    html2canvas(card, { scale: 2, useCORS: true }).then((canvas) => {
      const link = document.createElement('a');
      link.download = `invoice-meta-${formData.igHandle || formData.handle || 'user'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    });
  };

  const startOver = () => {
    localStorage.removeItem('invoiceMetaForm');
    window.location.href = '/';
  };

  const fmtCurrency = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n)
    .replace("US", "")
    .replace("USD", "")
    .trim();

  return (
    <ContentBox>
      {/* <div className="final-wrapper"> */}
      <div className="intro-main">
        <h1 style={{margin: 0}}>Your Invoice is Ready!</h1>


        <div style={{ marginTop: 25, width: '100%' }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span>Content Revenue</span>
                <span style={{ color: "#0198FF", fontWeight: 600 }}>
                  {fmtCurrency(result.contentRevenue)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span>Doomscroll Value</span>
                <span style={{ color: "#0198FF", fontWeight: 600 }}>
                  {fmtCurrency(result.scrollTimeValue)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span>Creative Labour Value</span>
                <span style={{ color: "#0198FF", fontWeight: 600 }}>
                  {fmtCurrency(result.creativeLabourValue)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span>Wellbeing Damages</span>
                <span style={{ color: "#0198FF", fontWeight: 600 }}>
                  $6,000
                </span>
              </div>

              <hr style={{ margin: "10px 0" }} />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                }}
              >
                <span></span>
                <span style={{ color: "#0198FF", fontSize: 20}}>
                  {fmtCurrency(result.total)}
                </span>
              </div>
            </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
          <button onClick={emailInvoice}>Send to Meta</button>
          <button style={{background: '#ddddddff', color: 'black'}} onClick={viewPdf}>View Invoice</button>
          {/* <button onClick={shareCard}>Share to Instagram</button> */}
        </div>
        
        <a href='https://www.youtube.com/watch?v=Jo8jmv4l2IE' target='_blank' className='link'>Ease Your Mind<ArrowForward></ArrowForward></a>
      </div>

      {/* Hidden Share Card ... (unchanged) */}
      <div id="share-card" /* ...snip... */ />
      {/* </div> */}
    </ContentBox>
  );
}
